import { useEffect } from "react";

import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { useStore } from "@/store";
import { useDirectChats } from "@/store/useDirectChats";

import { SectionDivider } from "../SectionDivider";
import { SidebarSectionHeader } from "../SidebarSectionHeader";
import { UnreadDot } from "../UnreadDot";
import { NavSection } from "../useSidebarNav";

import { DirectMessageRow } from "./DirectMessageRow";
import { NewDirectMessagePicker } from "./NewDirectMessagePicker";

import type { UserShort } from "@/api";
import type { ChatModel } from "@/models/chat/ChatModel";
import type { MemberModel } from "@/models/MemberModel";

interface DirectMessageEntry {
  user: UserShort;
  chat?: ChatModel;
}

function hasMessages(row: DirectMessageEntry): row is Required<DirectMessageEntry> {
  return (row.chat?.messagesCount ?? 0) > 0;
}

/**
 * One row per team member plus one per direct chat with someone outside the team. Rows whose chat
 * has messages come first, latest activity on top; then members in team order; then the empty
 * chats with non-members.
 */
function orderDirectMessageRows(
  members: MemberModel[],
  chats: ChatModel[],
  chatWith: (userUid: string) => ChatModel | undefined,
): DirectMessageEntry[] {
  const memberUids = new Set(members.map((member) => member.user.uid));
  const memberRows = members.map(({ user }) => ({ user, chat: chatWith(user.uid) }));
  const outsiderRows = chats.flatMap((chat) => {
    const user = chat.otherParticipant;
    return user && !memberUids.has(user.uid) ? [{ user, chat }] : [];
  });
  const rows = [...memberRows, ...outsiderRows];

  const active = rows
    .filter(hasMessages)
    .sort((a, b) => b.chat.lastActivityCursor.localeCompare(a.chat.lastActivityCursor));
  return [...active, ...rows.filter((row) => !hasMessages(row))];
}

export const DirectMessagesSection = observer(function DirectMessagesSection() {
  const t = useTranslations();
  const { auth, teams } = useStore();
  const directChats = useDirectChats();
  const team = teams.currentTeam;
  const isPersonal = team?.personal ?? true;
  const currentUserUid = auth.currentUser?.uid;

  useEffect(() => {
    void directChats.fetchChats();
  }, [currentUserUid, directChats]);

  useEffect(() => {
    if (!team || isPersonal || team.membersUX.membersStatus !== "init") return;

    team.membersUX.loadMembers().catch((err) => {
      console.error("Failed to load team members:", err);
    });
  }, [team, isPersonal]);

  if (!team) return null;

  const isOpen = directChats.isSectionOpen;
  // The personal space has no members to list, so it shows the conversations from every team
  const rows = isPersonal
    ? directChats.chats.flatMap((chat) => {
        const user = chat.otherParticipant;
        return user && chat.messagesCount > 0 ? [{ user, chat }] : [];
      })
    : orderDirectMessageRows(team.membersUX.members, directChats.chats, directChats.chatWith);

  return (
    <NavSection
      className="mt-1.25 p-1"
      aria-label={t("documentsSidebar.directMessages")}
      expanded={isOpen}
      onToggle={() => directChats.toggleSection()}
    >
      <SidebarSectionHeader
        compact
        isOpen={isOpen}
        onToggle={() => directChats.toggleSection()}
        title={t("documentsSidebar.directMessages")}
      >
        {directChats.hasUnreadChats && (
          <UnreadDot data-testid="direct-messages-unread-dot" className="my-auto mr-2" />
        )}
        <NewDirectMessagePicker team={isPersonal ? undefined : team} />
      </SidebarSectionHeader>
      {isOpen && rows.length > 0 && (
        <>
          <SectionDivider className="mt-1 mb-[3px]" />
          <ul role="group">
            {rows.map(({ user, chat }) => (
              <DirectMessageRow key={user.uid} user={user} chat={chat} />
            ))}
          </ul>
        </>
      )}
      {isOpen && isPersonal && rows.length === 0 && directChats.status === "loaded" && (
        <>
          <SectionDivider className="mt-1 mb-[3px]" />
          <StartDirectMessageHint />
        </>
      )}
    </NavSection>
  );
});

/** Points up at the header's new message action. */
function StartDirectMessageHint() {
  const t = useTranslations();

  return (
    <div className="flex items-center gap-1 pl-2.5 pr-0.5 text-gray-500">
      <p className="py-[5px] font-geist text-[16px] leading-[20px] tracking-tight">
        {t("documentsSidebar.startDirectMessageHint")}
      </p>
      <svg
        aria-hidden
        viewBox="0 0 64 28"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="ml-auto w-16 h-7 shrink-0 -mt-3"
      >
        <path d="M2 22c26 4 44 0 50-17" />
        <path d="M46.5 8.5L52 3.5l3.5 6.5" />
      </svg>
    </div>
  );
}
